"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getSubscription } from "../../services/subscriptionService";
import { api } from "../../services/api";
import {
  FiTruck, FiDollarSign, FiCalendar, FiTrendingUp, FiUsers,
  FiMapPin, FiClock, FiStar, FiArrowUp, FiArrowDown, FiAlertCircle, 
  FiPlus, FiChevronLeft, FiChevronRight, FiZap, FiEye,
  FiSettings
} from "react-icons/fi"
import { motion, AnimatePresence } from "framer-motion"
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// -- Sous-composants UI --
const StatCard = ({ icon, label, value, change, changeType, color, gradient, onClick, delay = 0 }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay, ease: "easeOut" }}
        onClick={onClick}
        whileHover={{ y: -8, scale: 1.05 }}
        className="bg-gradient-to-br from-slate-800/90 via-slate-800/70 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 shadow-2xl hover:shadow-amber-500/10 hover:border-amber-500/30 transition-all duration-300 cursor-pointer group"
    >
        <div className="flex items-center justify-between mb-6">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br ${gradient} shadow-lg ring-2 ring-amber-500/20 group-hover:ring-amber-500/40 transition-all duration-300`}>
                {icon}
            </div>
            {change && (
            <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: delay + 0.2, type: "spring", stiffness: 200 }}
                className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-bold ${ changeType === "up" ? "bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30" : "bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 border border-red-500/30" }`}
            >
                {changeType === "up" ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />} {change}
            </motion.div>
            )}
        </div>
        <div className="space-y-2">
            <p className="text-4xl font-bold text-white tracking-tight">{value}</p>
            <p className="text-sm font-semibold text-slate-300 tracking-wide">{label}</p>
        </div>
    </motion.div>
)

const MissionRow = ({ mission, onClick, index = 0 }) => {
    const getStatusConfig = (status) => {
        const configs = {
            'scheduled': { color: 'bg-gradient-to-r from-amber-500 to-amber-600', textColor: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', label: 'Planifié' },
            'in_progress': { color: 'bg-gradient-to-r from-blue-500 to-blue-600', textColor: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30', label: 'En cours' },
            'completed': { color: 'bg-gradient-to-r from-green-500 to-green-600', textColor: 'text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30', label: 'Terminé' },
            'pending': { color: 'bg-gradient-to-r from-purple-500 to-purple-600', textColor: 'text-purple-400', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30', label: 'En attente' }
        };
        return configs[status] || configs['pending'];
    };

    const statusConfig = getStatusConfig(mission.status);
    const scheduledDate = mission.scheduled_date ? new Date(mission.scheduled_date) : null;
    const formattedDate = scheduledDate ? format(scheduledDate, 'dd MMM yyyy', { locale: fr }) : 'Date non définie';
    const formattedTime = scheduledDate ? format(scheduledDate, 'HH:mm') : '';
    const price = mission.price_cents ? (mission.price_cents / 100).toLocaleString('fr-TN') : '0';
    const clientName = mission.client_name || 'Client inconnu';
    const fromAddress = mission.from_address || 'Adresse inconnue';
    const toAddress = mission.to_address || 'Adresse inconnue';

    return (
        <motion.button 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            onClick={onClick} 
            className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-slate-800/50 transition-all duration-300 text-left border border-slate-700/50 hover:border-slate-600/70 hover:shadow-lg group"
        >
            <div className="flex items-center gap-4 flex-1">
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-white group-hover:text-amber-300 transition-colors truncate">{clientName}</p>
                    <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                            <FiMapPin className="w-3 h-3 text-slate-500"/>
                            <span className="text-xs text-slate-400 truncate">{fromAddress} → {toAddress}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.borderColor} border`}>
                            {statusConfig.label}
                        </span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-4">
                <div className="text-right">
                    <p className="text-sm text-slate-300 font-medium">{formattedDate}</p>
                    {formattedTime && <p className="text-xs text-slate-500">{formattedTime}</p>}
                </div>
                <div className="text-right">
                    <p className="font-bold text-amber-400 text-lg">{price} TND</p>
                </div>
            </div>
        </motion.button>
    );
}

const QuickActionCard = ({ icon, label, description, color, onClick, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        onClick={onClick}
        whileHover={{ y: -5, scale: 1.02 }}
        className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-5 shadow-xl hover:shadow-amber-500/10 hover:border-amber-500/30 transition-all duration-300 cursor-pointer group"
    >
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${color} shadow-lg mb-4 group-hover:scale-110 transition-transform duration-300`}>
            {icon}
        </div>
        <h4 className="font-bold text-white mb-1 group-hover:text-amber-300 transition-colors">{label}</h4>
        <p className="text-sm text-slate-400">{description}</p>
    </motion.div>
);

const CalendarDay = ({ day, isToday, isAvailable, hasMission, onClick }) => {
    const baseClasses = "flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold transition-all duration-200 relative";
    let stateClasses = 'text-slate-500';
    if (isAvailable) stateClasses = 'bg-green-500/10 text-green-300 hover:bg-green-500/20 cursor-pointer';
    if (isToday) stateClasses = 'bg-gradient-to-br from-amber-500 to-amber-600 text-white font-bold hover:from-amber-400 hover:to-amber-500 cursor-pointer shadow-lg';

    return (
        <motion.button 
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClick} 
            className={`${baseClasses} ${stateClasses}`}
        >
            {day}
            {hasMission && <div className="absolute bottom-1 w-1.5 h-1.5 bg-blue-400 rounded-full shadow-sm"></div>}
        </motion.button>
    );
}

// --- COMPOSANT PRINCIPAL ---
export default function DemHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    missionsThisMonth: 0,
    revenueThisMonth: 0,
    averageRating: 0,
    newClients: 0
  });
  const [recentMissions, setRecentMissions] = useState([]);
  const [inProgressMissions, setInProgressMissions] = useState([]);
  const [missionDates, setMissionDates] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  
  // Attente initiale: tant que l'utilisateur n'est pas chargé, éviter d'afficher des infos basées sur essai
  const isUserLoading = !user || !user.id;
  
  // Charger toutes les données
  useEffect(() => {
    const fetchAllData = async () => {
      if (!user?.id) {
        console.log('❌ Pas d\'utilisateur, arrêt du chargement');
        return;
      }
      
      console.log('🚀 Début du chargement des données pour l\'utilisateur:', user.id);
      setIsLoading(true);
      
      try {
        console.log('📡 Appel des APIs en parallèle...');
        
        // Charger les données en parallèle
        const [subData, missionsData, datesData, availData] = await Promise.all([
          getSubscription(user.id).catch((err) => {
            console.warn('⚠️ Erreur récupération abonnement:', err);
            return null;
          }),
          fetchMoverMissions().catch((err) => {
            console.error('❌ Erreur récupération missions:', err);
            return [];
          }),
          fetchMissionDates().catch((err) => {
            console.warn('⚠️ Erreur récupération dates missions:', err);
            return [];
          }),
          fetchAvailabilities().catch((err) => {
            console.warn('⚠️ Erreur récupération disponibilités:', err);
            return [];
          })
        ]);
        
        console.log('✅ Données récupérées:', {
          subscription: subData ? 'Oui' : 'Non',
          missionsCount: missionsData.length,
          datesCount: datesData.length,
          availabilitiesCount: availData.length
        });
        
        setSubscription(subData);
        
        console.log('📦 Détails des missions récupérées:', {
          total: missionsData.length,
          missions: missionsData.map(m => ({
            id: m.id,
            status: m.status,
            scheduled_date: m.scheduled_date,
            completed_at: m.completed_at,
            created_at: m.created_at,
            price_cents: m.price_cents,
            client_id: m.client_id
          }))
        });
        
        // Séparer les missions en cours et les missions récentes
        const inProgress = missionsData.filter(m => m.status === 'in_progress');
        const upcoming = missionsData.filter(m => m.status === 'scheduled' || m.status === 'pending').slice(0, 5);
        
        console.log('📊 Répartition des missions:', {
          total: missionsData.length,
          inProgress: inProgress.length,
          upcoming: upcoming.length,
          completed: missionsData.filter(m => m.status === 'completed').length,
          scheduled: missionsData.filter(m => m.status === 'scheduled').length,
          pending: missionsData.filter(m => m.status === 'pending').length
        });
        
        setInProgressMissions(inProgress);
        setRecentMissions(upcoming);
        setMissionDates(datesData);
        setAvailabilities(availData);
        
        // Calculer les statistiques
        console.log('🧮 Calcul des statistiques avec', missionsData.length, 'missions...');
        // Passer les missions normalisées au calcul des stats
        calculateStats(missionsData);
        
        console.log('✅ Chargement terminé avec succès');
      } catch (error) {
        console.error('❌ Erreur lors du chargement des données:', error);
        console.error('Stack:', error.stack);
      } finally {
        setIsLoading(false);
        console.log('🏁 Chargement terminé (isLoading = false)');
      }
    };
    fetchAllData();
  }, [user?.id]);

  // Fonction pour récupérer les missions du déménageur
  const fetchMoverMissions = async () => {
    console.log('🔍 ========== DÉBUT RÉCUPÉRATION MISSIONS ==========');
    
    try {
      // Utiliser la même méthode que Missions.js qui fonctionne
      console.log('🔄 Utilisation de fetchMyMoverQuotes (comme dans Missions.js)...');
      
      const mineRes = await api.fetchMyMoverQuotes();
      console.log('📦 Réponse de fetchMyMoverQuotes:', {
        type: typeof mineRes,
        isArray: Array.isArray(mineRes),
        hasData: !!mineRes?.data,
        response: mineRes
      });
      
      // La réponse peut être directement un tableau ou dans response.data
      let missions = null;
      if (Array.isArray(mineRes)) {
        console.log('✅ Réponse est un tableau direct');
        missions = mineRes;
      } else if (mineRes?.data && Array.isArray(mineRes.data)) {
        console.log('✅ Réponse contient data (tableau)');
        missions = mineRes.data;
      } else {
        console.warn('⚠️ Format de réponse inattendu:', mineRes);
        missions = [];
      }
      
      if (missions && missions.length > 0) {
        console.log('✅ Missions récupérées via fetchMyMoverQuotes:', missions.length);
        console.log('📋 Détails des missions:', missions.slice(0, 5).map(m => ({
          id: m.id,
          status: m.status,
          move_date: m.move_date,
          scheduled_date: m.scheduled_date,
          completed_at: m.completed_at,
          created_at: m.created_at,
          price_cents: m.price_cents,
          from_address: m.from_address,
          to_address: m.to_address
        })));
        
        // Normaliser les missions pour avoir un format cohérent
        const normalizedMissions = missions.map(m => ({
          id: m.id,
          client_id: m.client_id,
          mover_id: m.mover_id,
          quote_id: m.quote_id || m.id,
          scheduled_date: m.scheduled_date || m.move_date || m.created_at,
          status: m.status || 'pending',
          started_at: m.started_at,
          completed_at: m.completed_at,
          created_at: m.created_at,
          from_address: m.from_address,
          to_address: m.to_address,
          volume_m3: m.volume_m3,
          truck_type: m.truck_type,
          price_cents: m.price_cents,
          client_name: m.user?.first_name && m.user?.last_name 
            ? `${m.user.first_name} ${m.user.last_name}`
            : m.client_name || 'Client inconnu',
          description: m.description,
          housing_type: m.housing_type,
          floor: m.floor,
          has_elevator: m.has_elevator,
          services: m.services
        }));
        
        // Trier par date décroissante
        const sortedMissions = normalizedMissions.sort((a, b) => {
          const dateA = new Date(a.scheduled_date || a.completed_at || a.created_at || 0);
          const dateB = new Date(b.scheduled_date || b.completed_at || b.created_at || 0);
          return dateB - dateA;
        });
        
        console.log('✅ Missions normalisées et triées:', sortedMissions.length);
        console.log('📋 Premières missions:', sortedMissions.slice(0, 3).map(m => ({
          id: m.id,
          status: m.status,
          date: m.scheduled_date || m.completed_at || m.created_at,
          client: m.client_name
        })));
        
        console.log('🔍 ========== FIN RÉCUPÉRATION MISSIONS ==========');
        return sortedMissions;
      } else {
        console.warn('⚠️ Aucune mission trouvée dans fetchMyMoverQuotes');
        console.log('🔍 ========== FIN RÉCUPÉRATION MISSIONS (vide) ==========');
        return [];
      }
    } catch (error) {
      console.error('❌ Erreur récupération missions:', error);
      console.error('📋 Détails:', {
        message: error.message,
        stack: error.stack
      });
      console.log('🔍 ========== FIN RÉCUPÉRATION MISSIONS (erreur) ==========');
      return [];
    }
  };

  // Fonction pour récupérer les dates de missions
  const fetchMissionDates = async () => {
    try {
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      const response = await api.getMyMissionDates(month, year);
      return response?.data || [];
    } catch (error) {
      console.error('Erreur récupération dates missions:', error);
      return [];
    }
  };

  // Fonction pour récupérer les disponibilités
  const fetchAvailabilities = async () => {
    try {
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      const response = await api.getMyAvailabilities(month, year);
      // La réponse est un objet avec les dates comme clés { "2025-01-15": true, ... }
      const data = response?.data || {};
      // Convertir l'objet en tableau de dates
      return Object.keys(data).map(date => ({ date, is_available: true }));
    } catch (error) {
      console.error('Erreur récupération disponibilités:', error);
      return [];
    }
  };

  // Fonction pour calculer les statistiques
  const calculateStats = (missions) => {
    console.log('🧮 ========== DÉBUT CALCUL STATISTIQUES ==========');
    console.log('📥 Missions reçues:', missions.length);
    
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
    
    console.log('📅 Période de calcul:', {
      today: today.toISOString(),
      firstDayOfMonth: firstDayOfMonth.toISOString(),
      lastDayOfMonth: lastDayOfMonth.toISOString(),
      month: today.getMonth() + 1,
      year: today.getFullYear()
    });
    
    // Afficher un échantillon des missions
    console.log('📋 Échantillon des missions (3 premières):', missions.slice(0, 3).map(m => ({
      id: m.id,
      status: m.status,
      scheduled_date: m.scheduled_date,
      move_date: m.move_date,
      completed_at: m.completed_at,
      created_at: m.created_at,
      price_cents: m.price_cents
    })));
    
    // Missions ce mois - utiliser scheduled_date, move_date, OU completed_at pour les missions terminées
    const missionsThisMonth = missions.filter((m, index) => {
      // Utiliser move_date si disponible (format quotes), sinon scheduled_date (format moves)
      const dateField = m.move_date || m.scheduled_date;
      
      console.log(`🔍 Mission ${index + 1}/${missions.length} (ID: ${m.id}):`, {
        status: m.status,
        move_date: m.move_date,
        scheduled_date: m.scheduled_date,
        completed_at: m.completed_at,
        created_at: m.created_at,
        dateField: dateField
      });
      
      if (!dateField && !m.completed_at && !m.created_at) {
        console.log(`  ❌ Pas de date disponible pour la mission ${m.id}`);
        return false;
      }
      
      // Pour les missions terminées, utiliser completed_at si disponible
      // Sinon utiliser move_date ou scheduled_date
      // Sinon utiliser created_at
      let missionDate = null;
      if (m.status === 'completed' && m.completed_at) {
        missionDate = new Date(m.completed_at);
        console.log(`  ✅ Mission terminée - utilisation de completed_at: ${missionDate.toISOString()}`);
      } else if (dateField) {
        missionDate = new Date(dateField);
        console.log(`  ✅ Utilisation de ${m.move_date ? 'move_date' : 'scheduled_date'}: ${missionDate.toISOString()}`);
      } else if (m.created_at) {
        missionDate = new Date(m.created_at);
        console.log(`  ✅ Utilisation de created_at: ${missionDate.toISOString()}`);
      }
      
      if (!missionDate || isNaN(missionDate.getTime())) {
        console.log(`  ❌ Date invalide pour la mission ${m.id}`);
        return false;
      }
      
      // Vérifier si la mission est dans le mois en cours
      const isInMonth = missionDate >= firstDayOfMonth && missionDate <= lastDayOfMonth;
      console.log(`  ${isInMonth ? '✅' : '❌'} Mission ${isInMonth ? 'dans' : 'hors'} du mois:`, {
        missionDate: missionDate.toISOString(),
        isInMonth,
        beforeFirstDay: missionDate < firstDayOfMonth,
        afterLastDay: missionDate > lastDayOfMonth
      });
      
      return isInMonth;
    });
    
    console.log('📊 Résultat du filtrage:', {
      totalMissions: missions.length,
      missionsThisMonth: missionsThisMonth.length,
      missionsDetails: missionsThisMonth.map(m => ({
        id: m.id,
        status: m.status,
        date: m.completed_at || m.move_date || m.scheduled_date || m.created_at,
        price_cents: m.price_cents
      }))
    });
    
    // Revenus ce mois (en TND) - missions terminées ou acceptées ce mois
    const completedMissions = missionsThisMonth.filter(m => 
      m.status === 'completed' || m.status === 'accepted'
    );
    console.log('💰 Missions terminées/acceptées ce mois:', completedMissions.length);
    
    const revenueThisMonth = completedMissions.reduce((sum, m) => {
      const price = m.price_cents || 0;
      console.log(`  💵 Mission ${m.id}: ${price} centimes`);
      return sum + price;
    }, 0) / 100;
    
    console.log('💰 Revenus totaux ce mois:', revenueThisMonth, 'TND');
    
    // Nouveaux clients (clients uniques ce mois)
    const clientsThisMonth = new Set(
      missionsThisMonth
        .map(m => m.client_id)
        .filter(Boolean)
    ).size;
    
    console.log('👥 Clients uniques ce mois:', clientsThisMonth);
    
    // Note moyenne (à récupérer depuis le profil si disponible)
    const averageRating = user?.rating || 0;
    console.log('⭐ Note moyenne:', averageRating);
    
    const finalStats = {
      missionsThisMonth: missionsThisMonth.length,
      revenueThisMonth,
      averageRating,
      newClients: clientsThisMonth
    };
    
    console.log('📊 ========== STATISTIQUES FINALES ==========');
    console.log(JSON.stringify(finalStats, null, 2));
    console.log('🧮 ========== FIN CALCUL STATISTIQUES ==========');
    
    setStats(finalStats);
  };
   
  // Vérifier si l'utilisateur est un déménageur
  const isDemenageur = user?.is_demenageur === true || user?.role === 'demenageur';
  
  // Récupérer trial_end_date depuis l'utilisateur
  let trialEndDate = null;
  if (user?.trial_end_date) {
    trialEndDate = new Date(user.trial_end_date);
  } else if (user?.trialEndDate) {
    trialEndDate = new Date(user.trialEndDate);
  }
  
  // Vérifier que la date est valide
  const isTrialDateValid = trialEndDate && !isNaN(trialEndDate.getTime()) && trialEndDate > new Date();
  const isSubscriptionTrial = !subscription || subscription.status === 'trial';
  const isTrialActive = isDemenageur && isTrialDateValid && isSubscriptionTrial;
  
  // Vérifier si la période d'essai est terminée
  const isTrialEnded = isDemenageur && trialEndDate && !isNaN(trialEndDate.getTime()) && trialEndDate <= new Date() && isSubscriptionTrial;
  
  // Calculer les jours restants de la période d'essai
  const trialDaysLeft = isTrialActive && trialEndDate && !isNaN(trialEndDate.getTime())
    ? Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  const handlePreviousMonth = () => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  
  const welcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bonjour,";
    if (hour < 18) return "Bon après-midi,";
    return "Bonsoir,";
  };
  
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = (firstDay === 0) ? 6 : firstDay - 1;
    
    // Vérifier si le mois affiché correspond au mois actuel
    const isCurrentMonth = year === new Date().getFullYear() && month === new Date().getMonth();
    
    let days = [];
    for (let i = 0; i < offset; i++) days.push(<div key={`empty-${i}`}></div>);
    for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(year, month, i);
        const dateString = date.toISOString().slice(0, 10);
        const hasMission = isCurrentMonth && missionDates.includes(dateString);
        const isAvailable = availabilities.some(a => {
          const availDate = a.date || a.available_date;
          if (!availDate) return false;
          const dateStr = typeof availDate === 'string' ? availDate : new Date(availDate).toISOString().slice(0, 10);
          return dateStr === dateString;
        });
        
        days.push(
          <CalendarDay 
            key={i} 
            day={i} 
            isToday={new Date().toDateString() === date.toDateString()} 
            isAvailable={isAvailable}
            hasMission={hasMission}
            onClick={() => setSelectedDate(date)}
          />
        );
    }
    return days;
  };

  const quickActions = [
    { icon: <FiPlus className="w-6 h-6 text-white"/>, label: "Nouvelle mission", description: "Créer un devis", color: "from-amber-500 to-amber-600", path: "/demenageur/devis" },
    { icon: <FiEye className="w-6 h-6 text-white"/>, label: "Voir missions", description: "Missions disponibles", color: "from-blue-500 to-blue-600", path: "/demenageur/missions" },
    { icon: <FiCalendar className="w-6 h-6 text-white"/>, label: "Disponibilités", description: "Gérer mon agenda", color: "from-green-500 to-green-600", path: "/demenageur/disponibilites" },
    { icon: <FiSettings className="w-6 h-6 text-white"/>, label: "Paramètres", description: "Mon profil", color: "from-purple-500 to-purple-600", path: "/demenageur/profil" },
  ];

  if (isUserLoading || isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-4 sm:p-6 lg:p-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-600/10"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]"></div>
      </div>
      <div className="max-w-7xl mx-auto space-y-8 relative z-10">
        {/* Bannière de période d'essai */}
        {isTrialEnded ? (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 bg-gradient-to-r from-red-600 via-red-700 to-amber-600 text-white p-6 rounded-3xl shadow-2xl border border-red-500/20 backdrop-blur-xl"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <FiAlertCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-xl">Période d'essai terminée</h3>
                  <p className="text-sm text-red-100">
                    Votre période d'essai gratuite est terminée. Pour continuer à utiliser nos services, veuillez souscrire à un abonnement.
                  </p>
                </div>
              </div>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/demenageur/contrat/abonnement')} 
                className="mt-3 md:mt-0 px-6 py-3 bg-white text-red-700 font-bold rounded-2xl hover:bg-red-50 transition-all duration-300 shadow-xl flex items-center gap-2"
              >
                <FiTrendingUp className="w-4 h-4" />
                S'abonner maintenant
              </motion.button>
            </div>
          </motion.div>
        ) : isTrialActive && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 bg-gradient-to-r from-blue-600 via-indigo-700 to-purple-700 text-white p-6 rounded-3xl shadow-2xl border border-blue-500/20 backdrop-blur-xl"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <FiClock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-xl">Période d'essai premium active</h3>
                  <p className="text-sm text-blue-100">
                    {trialDaysLeft > 1 
                      ? `Il vous reste ${trialDaysLeft} jours d'essai gratuit`
                      : 'Dernier jour de votre période d\'essai gratuite'}
                  </p>
                </div>
              </div>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/demenageur/contrat/abonnement')} 
                className="mt-3 md:mt-0 px-6 py-3 bg-white text-blue-700 font-bold rounded-2xl hover:bg-blue-50 transition-all duration-300 shadow-xl flex items-center gap-2"
              >
                <FiStar className="w-4 h-4" />
                Choisir un abonnement
              </motion.button>
            </div>
          </motion.div>
        )}

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FiTruck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white tracking-tight">{welcomeMessage()} DéménageurPro !</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"></div>
                    <p className="text-amber-400 font-semibold">Tableau de bord principal</p>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 mt-3">Voici le récapitulatif de votre activité et vos prochaines missions.</p>
            </div>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/demenageur/disponibilites')} 
              className="px-6 py-3 bg-gradient-to-r from-amber-500 via-amber-600 to-yellow-600 text-slate-900 font-bold rounded-2xl flex items-center gap-2 hover:shadow-xl hover:shadow-amber-500/25 transition-all duration-300 border-2 border-amber-400/20"
            >
              <FiPlus className="w-5 h-5"/> 
              Publier une disponibilité
            </motion.button>
          </div>
        </motion.header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              icon={<FiTruck className="w-6 h-6 text-white"/>} 
              label="Missions ce mois" 
              value={stats.missionsThisMonth} 
              gradient="from-blue-500 to-blue-600" 
              onClick={() => navigate('/demenageur/missions')} 
              delay={0}
            />
            <StatCard 
              icon={<FiDollarSign className="w-6 h-6 text-white"/>} 
              label="Revenus ce mois" 
              value={`${stats.revenueThisMonth.toLocaleString('fr-TN')} TND`} 
              gradient="from-green-500 to-green-600" 
              onClick={() => navigate('/demenageur/paiements')} 
              delay={0.1}
            />
            <StatCard 
              icon={<FiStar className="w-6 h-6 text-white"/>} 
              label="Note moyenne" 
              value={stats.averageRating > 0 ? `${stats.averageRating.toFixed(1)}/5` : 'N/A'} 
              gradient="from-yellow-500 to-yellow-600" 
              onClick={() => navigate('/demenageur/profil')} 
              delay={0.2}
            />
                <StatCard 
              icon={<FiUsers className="w-6 h-6 text-white"/>} 
              label="Nouveaux clients" 
              value={stats.newClients} 
              gradient="from-purple-500 to-purple-600" 
              onClick={() => navigate('/demenageur/clients')} 
              delay={0.3}
            />
        </div>

        {/* Actions Rapides */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FiZap className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Actions Rapides</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => (
              <QuickActionCard
                key={`action-${index}`}
                {...action}
                onClick={() => navigate(action.path)}
                delay={index * 0.1}
              />
            ))}
          </div>
        </motion.section>

        {/* Missions en cours - Section prioritaire */}
        {inProgressMissions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-purple-600/20 backdrop-blur-xl border-2 border-blue-500/30 rounded-3xl p-6 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg ring-4 ring-blue-500/30"
                  >
                    <FiTruck className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-2xl font-bold text-white tracking-wide flex items-center gap-2">
                      Missions en cours
                      <motion.span
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-2 h-2 bg-blue-400 rounded-full"
                      ></motion.span>
                    </h3>
                    <p className="text-sm text-blue-300/80 font-medium">{inProgressMissions.length} mission{inProgressMissions.length !== 1 ? 's' : ''} active{inProgressMissions.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/demenageur/missions')} 
                  className="text-sm font-bold text-white hover:text-blue-200 transition-colors bg-blue-500/20 hover:bg-blue-500/30 px-4 py-2 rounded-xl border border-blue-400/50 hover:border-blue-400/70"
                >
                  Voir tout
                </motion.button>
              </div>
              <div className="space-y-3">
                {inProgressMissions.map((mission, index) => (
                  <motion.div
                    key={mission.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border-2 border-blue-500/30 rounded-2xl p-4 hover:border-blue-400/50 transition-all duration-300 cursor-pointer group"
                    onClick={() => navigate(`/demenageur/missions/${mission.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-3 h-3 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full flex-shrink-0 shadow-lg"
                        ></motion.div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white group-hover:text-blue-200 transition-colors truncate">
                            {mission.client_name || 'Client inconnu'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <FiMapPin className="w-3 h-3 text-blue-400"/>
                            <span className="text-xs text-blue-300/80 truncate">
                              {mission.from_address || 'Départ'} → {mission.to_address || 'Arrivée'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-blue-200 font-medium">
                            {mission.scheduled_date ? format(new Date(mission.scheduled_date), 'HH:mm', { locale: fr }) : '—'}
                          </p>
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-500/20 text-blue-300 border border-blue-400/30">
                            En cours
                          </span>
                        </div>
                        {mission.price_cents && (
                          <div className="text-right">
                            <p className="font-bold text-blue-300 text-lg">
                              {(mission.price_cents / 100).toLocaleString('fr-TN')} TND
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Missions à venir */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="lg:col-span-2 bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-amber-500/20">
                  <FiCalendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white tracking-wide">Missions à venir</h3>
                  <p className="text-sm text-amber-300/80 font-medium">{recentMissions.length} mission{recentMissions.length !== 1 ? 's' : ''} planifiée{recentMissions.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/demenageur/missions')} 
                className="text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-4 py-2 rounded-xl border border-amber-500/30 hover:border-amber-500/50"
              >
                Voir tout
              </motion.button>
            </div>
            <div className="space-y-3">
              {recentMissions.length > 0 ? (
                recentMissions.map((mission, index) => (
                <MissionRow 
                  key={mission.id} 
                  mission={mission} 
                  onClick={() => navigate(`/demenageur/missions/${mission.id}`)} 
                  index={index}
                />
                ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <FiTruck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune mission planifiée</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Calendrier */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-green-500/20">
                <FiCalendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white tracking-wide">Vos Disponibilités</h3>
                <p className="text-sm text-green-300/80 font-medium">Gérez votre agenda</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700/50 to-slate-800/50 p-5 rounded-2xl border border-slate-600/50">
              <div className="flex items-center justify-between text-sm font-bold text-white mb-4">
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePreviousMonth} 
                  className="p-2 rounded-xl hover:bg-slate-600/50 transition-colors"
                >
                  <FiChevronLeft className="w-5 h-5"/>
                </motion.button>
                <span className="text-lg">{format(currentDate, 'MMMM yyyy', { locale: fr })}</span>
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleNextMonth} 
                  className="p-2 rounded-xl hover:bg-slate-600/50 transition-colors"
                >
                  <FiChevronRight className="w-5 h-5"/>
                </motion.button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400 mb-3">
                <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
              </div>
              <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* --- MODALE DÉTAILS DU JOUR --- */}
       <AnimatePresence>
          {selectedDate && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" 
                onClick={() => setSelectedDate(null)}
              >
                  <motion.div 
                    initial={{ scale: 0.9, y: 20 }} 
                    animate={{ scale: 1, y: 0 }} 
                    exit={{ scale: 0.9, y: 20 }} 
                    onClick={(e) => e.stopPropagation()} 
                    className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-md"
                  >
                      <div className="p-6 border-b border-gradient-to-r from-slate-700/80 via-green-500/20 to-slate-700/80 bg-gradient-to-r from-slate-800/50 to-slate-900/30">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <FiCalendar className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-white">Détails du {format(selectedDate, 'd MMMM yyyy', { locale: fr })}</h3>
                        </div>
                      </div>
                      <div className="p-6 space-y-4">
                        <p className="text-slate-300">Missions et disponibilités pour ce jour...</p>
                      </div>
                      <div className="p-6 flex justify-end gap-3 bg-gradient-to-r from-slate-800/30 to-slate-900/30 rounded-b-3xl">
                        <motion.button 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedDate(null)} 
                          className="px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 border border-slate-600/50"
                        >
                          Fermer
                        </motion.button>
                      </div>
                  </motion.div>
              </motion.div>
          )}
      </AnimatePresence>
    </div>
  )
}
